/**
 * features/hr/LeaveBalances.tsx
 * ------------------------------------------------------------------
 * HR PAYROLL — leave balances + entitlements editor (Phase 1, Day 4).
 *
 * Shows the per-type balance table (entitlement / taken / remaining)
 * on a staff member's Leave tab, and lets HR adjust that staff's
 * annual entitlements (writing hrLeaveAllocations/<staffId>).
 *
 * Balances are derived (see lib/hrLeave.computeLeaveBalances) — there
 * is no stored running total to drift. Defaults are 18 annual / 20
 * sick until an allocation is saved. The same numbers print on the
 * payslip (Day 6).
 * ------------------------------------------------------------------ */
import LeaveCardPrint from '@/features/hr/LeaveCardPrint';
import { useState } from 'react';
import { CalendarDays, Pencil, Save, Loader2, Plus, Trash2 } from 'lucide-react';
import { SoftCard, AccentButton, glassFieldClass } from '@/features/finance/ui';
import { FormModal } from '@/components/ui/glass';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { hr } from '@/features/hr/hrWrite';
import { LEAVE_TYPE_LABELS, type HrStaff } from '@/lib/hr';
import { effectiveEntitlements, type LeaveRequestLike } from '@/lib/hrLeave';
import { DEFAULT_LEAVE_ENTITLEMENTS, type HrLeaveAllocation, type LeaveBalance } from '@/lib/hrPayroll';
import { leaveBalancesFor, currentLeaveYear } from '@/lib/leavePolicy';
import { useLeavePolicies } from '@/features/hr/useLeavePolicies';
import type { EncashmentLike } from '@/lib/hrLeave';

function typeLabel(type: string): string {
  return LEAVE_TYPE_LABELS[type as keyof typeof LEAVE_TYPE_LABELS] ?? type;
}

export default function LeaveBalances({
  staff,
  leave,
  allocation,
  canEdit,
  encashments,
}: {
  staff: HrStaff;
  leave: LeaveRequestLike[];
  allocation?: HrLeaveAllocation;
  canEdit: boolean;
  /** This person's leave sales. */
  encashments?: EncashmentLike[];
}) {
  const [editing, setEditing] = useState(false);
  const { policies } = useLeavePolicies();
  const year = allocation?.year ?? currentLeaveYear();
  const balances = leaveBalancesFor({ category: staff.category, allocation, requests: leave, policies, year, employmentStart: staff.startDate, encashments });
  const annual = balances.find((b) => b.type === 'annual');
  const sick = balances.find((b) => b.type === 'sick');

  return (
    <SoftCard hover={false}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
          <CalendarDays size={16} /> Leave balances
          <span className="text-xs font-normal text-gray-400">· {year}</span>
        </h3>
        <div className="flex items-center gap-3">
        <LeaveCardPrint staff={[staff]} compact />
        {canEdit && (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-1 text-xs font-medium text-[var(--accent-link,#2d5295)] hover:underline dark:text-[var(--accent-link-dark,#93c5fd)]"
          >
            <Pencil size={12} /> Edit entitlements
          </button>
        )}
        </div>
      </div>

      {/* Prominent headline balances */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <HeadlineBalance label="Annual leave left" b={annual} />
        <HeadlineBalance label="Sick leave left" b={sick} />
      </div>

      {!allocation && (
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-2">
          No per-staff override — using the cohort / default entitlement
          ({DEFAULT_LEAVE_ENTITLEMENTS.annual} annual · {DEFAULT_LEAVE_ENTITLEMENTS.sick} sick unless the cohort sets otherwise).
          Edit to set this staff member’s own entitlement.
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
              <th className="py-2 pr-4 font-medium">Type</th>
              <th className="py-2 pr-4 font-medium text-right">Entitlement</th>
              <th className="py-2 pr-4 font-medium text-right">Taken</th>
              <th className="py-2 font-medium text-right">Remaining</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {balances.map((b) => (
              <BalanceRow key={b.type} b={b} />
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EntitlementsModal
          staff={staff}
          allocation={allocation}
          year={year}
          onClose={() => setEditing(false)}
        />
      )}
    </SoftCard>
  );
}

function HeadlineBalance({ label, b }: { label: string; b?: LeaveBalance }) {
  const remainingDays = b?.remaining ?? 0;
  const negative = remainingDays < 0;
  const low = !negative && (b?.entitlement ?? 0) > 0 && remainingDays <= (b!.entitlement) * 0.15;
  const tone = negative
    ? 'text-red-600 dark:text-red-400'
    : low ? 'text-amber-600 dark:text-amber-400' : 'text-[var(--accent-strong,#1b3a6b)] dark:text-[var(--accent-link-dark,#93c5fd)]';
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className="flex items-baseline gap-1.5">
        <span className={`text-3xl font-extrabold tabular-nums ${tone}`}>{b ? remainingDays : '—'}</span>
        <span className="text-sm text-gray-500 dark:text-gray-400">/ {b?.entitlement ?? 0} days</span>
      </div>
      {/* Sold days shown on their own. The balance above is already net of
          them; without this line "taken 0, balance 5" on a year's entitlement
          reads as days gone missing. */}
      {b && <div className="text-[11px] text-gray-400">{b.taken} taken{b.sold ? ` · ${b.sold} sold` : ''}{b.opening ? ` · ${b.opening > 0 ? '+' : ''}${b.opening} brought in` : ''}</div>}
    </div>
  );
}

function BalanceRow({ b }: { b: LeaveBalance }) {
  const negative = b.remaining < 0;
  const low = !negative && b.entitlement > 0 && b.remaining <= b.entitlement * 0.15;
  const remCls = negative
    ? 'text-red-600 dark:text-red-400'
    : low
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-gray-800 dark:text-gray-100';
  return (
    <tr>
      <td className="py-2 pr-4 text-gray-700 dark:text-gray-200">{typeLabel(b.type)}</td>
      <td className="py-2 pr-4 text-right text-gray-600 dark:text-gray-300">
        {b.entitlement > 0 ? `${b.entitlement} days` : '—'}
      </td>
      <td className="py-2 pr-4 text-right text-gray-600 dark:text-gray-300">{b.taken} days</td>
      <td className={`py-2 text-right font-semibold ${remCls}`}>{b.remaining} days</td>
    </tr>
  );
}

/* ==================================================================
   ENTITLEMENTS EDITOR
   ================================================================== */
type Row = { type: string; days: string; opening: string };

const KNOWN_TYPES = ['annual', 'sick', 'maternity', 'paternity', 'compassionate', 'study', 'unpaid'];

function EntitlementsModal({
  staff,
  allocation,
  year,
  onClose,
}: {
  staff: HrStaff;
  allocation?: HrLeaveAllocation;
  year: string;
  onClose: () => void;
}) {
  const seed = effectiveEntitlements(allocation);
  const openingSeed = allocation?.openingBalances ?? {};
  const [rows, setRows] = useState<Row[]>(
    Object.entries(seed).map(([type, days]) => ({
      type,
      days: String(days),
      opening: openingSeed[type] != null ? String(openingSeed[type]) : '',
    })),
  );
  const [saving, setSaving] = useState(false);
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>(
    { show: false, status: 'saving', message: '' },
  );

  function setRow(i: number, patch: Partial<Row>) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function addRow() {
    const used = new Set(rows.map((r) => r.type));
    const next = KNOWN_TYPES.find((t) => !used.has(t)) ?? '';
    setRows((prev) => [...prev, { type: next, days: '0', opening: '' }]);
  }
  function removeRow(i: number) {
    setRows((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function save() {
    setSaving(true);
    setSaveModal({ show: true, status: 'saving', message: 'Saving entitlements…' });
    try {
      const entitlements: Record<string, number> = {};
      const openingBalances: Record<string, number> = {};
      for (const r of rows) {
        const t = r.type.trim();
        if (!t) continue;
        const d = Number(r.days);
        entitlements[t] = Number.isFinite(d) ? d : 0;
        const o = Number(r.opening);
        if (r.opening.trim() !== '' && Number.isFinite(o) && o !== 0) openingBalances[t] = o;
      }
      await hr.leaveAllocations.set(staff.id, {
        staffId: staff.id,
        entitlements,
        openingBalances,
        year,
      });
      setSaveModal({ show: true, status: 'success', message: 'Leave entitlements saved.' });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <FormModal
        onClose={onClose}
        icon={CalendarDays}
        title={`Leave entitlements · ${year}`}
        subtitle={`${staff.firstName} ${staff.lastName}`}
        maxWidth="max-w-md"
        footer={
          <>
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl dark:text-gray-300 dark:hover:bg-gray-700">Cancel</button>
            <AccentButton icon={saving ? Loader2 : Save} disabled={saving} onClick={save}>
              {saving ? 'Saving…' : 'Save entitlements'}
            </AccentButton>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Annual days granted per leave type. Remaining balances are computed from approved leave.
            The <span className="font-medium">opening</span> column carries in a balance at go-live (+ brought forward, − already taken this year).
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-2 px-0.5">
              <span className="flex-1 text-[11px] font-medium text-gray-400 uppercase tracking-wide">Type</span>
              <span className="w-24 text-[11px] font-medium text-gray-400 uppercase tracking-wide">Days/yr</span>
              <span className="w-24 text-[11px] font-medium text-gray-400 uppercase tracking-wide">Opening</span>
              <span className="w-7" />
            </div>
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <select
                  value={r.type}
                  onChange={(e) => setRow(i, { type: e.target.value })}
                  className={glassFieldClass('py-1.5 text-sm flex-1')}
                >
                  {!KNOWN_TYPES.includes(r.type) && r.type && <option value={r.type}>{typeLabel(r.type)}</option>}
                  {KNOWN_TYPES.map((t) => (
                    <option key={t} value={t}>{typeLabel(t)}</option>
                  ))}
                </select>
                <input
                  type="number"
                  inputMode="numeric"
                  value={r.days}
                  onChange={(e) => setRow(i, { days: e.target.value })}
                  className={glassFieldClass('py-1.5 text-sm w-24')}
                />
                <input
                  type="number"
                  inputMode="numeric"
                  value={r.opening}
                  onChange={(e) => setRow(i, { opening: e.target.value })}
                  placeholder="0"
                  className={glassFieldClass('py-1.5 text-sm w-24')}
                />
                <button onClick={() => removeRow(i)} className="text-gray-400 hover:text-red-500 p-1" title="Remove">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
          <button onClick={addRow} className="flex items-center gap-1 text-xs font-medium text-[var(--accent-link,#2d5295)] hover:underline dark:text-[var(--accent-link-dark,#93c5fd)]">
            <Plus size={12} /> Add leave type
          </button>
        </div>
      </FormModal>

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => {
          if (saveModal.status === 'success') onClose();
          else setSaveModal((m) => ({ ...m, show: false }));
        }}
      />
    </>
  );
}
